---
title: Defaults and limits
lede: How many sessions run at once, when an idle one gives its memory back, and every bound Airlock enforces.
order: 49
part: Teams and settings
---

Two ceilings decide when a new task may start, and a timer decides when a finished session's machine is stopped. You set all three in **Settings › Sessions**, and they count every workspace together.

![Session settings](media/settings-sessions.webp)

### The two ceilings

**Sessions working at once** (default 3, 1–10) counts agents that are queued, starting, running or merging. It also counts sessions that have handed back and are waiting on you, because answering one puts an agent back to work.

**Sessions open at once** (default 6, 1–30) counts machines that are up, whatever they are doing. A finished session keeps its machine until you delete it or it is parked.

When a ceiling is reached, a new task waits at the top of **To do** and the board's *In development* lane shows `3/3 BUSY`. The task says why:

- *2 working and 1 waiting on you, of 3. Answer one, or merge or delete it, to free a slot.*
- *3 sessions already working (limit 3).*
- *No machine free (6 of 6). Delete a session to start this.*

A task marked **Urgent** starts anyway.

### Parking an idle machine

**Park an idle machine after** (default 30 minutes, 0–1440) stops the machine of a session that has handed back and sat untouched that long. Its disk, its work and the agent's conversation are kept, and opening the session wakes it in seconds. `0` never parks. Until the machine is back the session shows only that it is parked or waking: none of its tabs can be opened.

A parked session counts against neither ceiling, and neither does a session that runs [on this computer](/placement) rather than in a micro-VM: it holds no machine, so a task placed there starts even while the board is full.

### Session settings

| Setting | Default | Range |
|---|---|---|
| **Sessions start on** | Opus · X-High | Any enabled model |
| **Source branch** | `main` | A valid branch name |
| **Sessions working at once** | 3 | 1–10 |
| **Sessions open at once** | 6 | 1–30 |
| **Park an idle machine after** | 30 minutes | 0–1440 |
| **Keep archived tasks for** | 30 days | 1–365 |
| **Idle fallback** | 20 seconds | 5–600 |
| **Check for new work every** (Jira, Trello) | 10 minutes | 2–120 |

### Machine settings

| Setting | Default | Range |
|---|---|---|
| **CPU cores** | 6 | 1 to half this computer's cores (16 at most) |
| **Memory** | 12288 MB | 1024 MB to half this computer's memory (65536 MB at most), steps of 512 |
| **Disk** | 10 GB | 5 GB to half the free disk (500 GB at most) |
| **Docker disk** | 20 GB | 5–500 GB |
| **Largest repository history** | 512 MB | 64–8192 MB |
| **Images kept** | 5 | 1–20 |
| **Retention per log file** | 256 MB | 16–8192 MB |
| **Lines kept in the logs dock** | 2000 | 200–50000, steps of 100 |

A micro-VM boots with 3072 MB of memory and grows toward **Memory** only when it needs to.

### Other bounds

| What | Limit |
|---|---|
| Additional hosts, per list (application or workspace) | 64 |
| Resolvers | 6 |
| MCP servers / skills / plugins | 20 / 40 / 20 |
| Repositories per workspace | 64 |
| Environment variables per repository | 200 |
| Services on this computer, per workspace | 8 |
| Session image (Dockerfile) | 20000 characters |
| Workflows in a library | 20 |
| Steps per workflow | 30 |
| Times a run may enter one step | 5 |
| Agents a session's agent may start | 3 at once, 12 in total |
| API console environments / variables, per project | 20 / 200 |
| API console flows per project | 40 |
| Files attached to a task | 12, at most 10 MB each and 25 MB together |
| Tickets imported per pass | 50 |

### What a workspace can override

A workspace can answer **Sessions start on**, **Source branch**, **Keep archived tasks for**, **Idle fallback**, the machine sizes and both log settings for itself. The two ceilings, parking, **Images kept** and the sync interval belong to the whole app. See [Workspace settings](/workspace-settings).

![Workspace session settings](media/ws-sessions.webp)
